const GTM_SCRIPT_ID = "google-tag-manager-loader-script"

/** Matches Google Tag Manager web container IDs. */
const isValidGtmContainerId = (id: string): boolean => /^GTM-[A-Z0-9]+$/i.test(id.trim())

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
  }
}

/**
 * Loads GTM after React mounts (website layout only).
 * Uses a dynamic script tag with onerror so ad blockers surface as a single info line
 * instead of an uncaught network failure on the injected URL.
 */
export const initGoogleTagManager = (): void => {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return
    }

    const fromEnv = import.meta.env.WEBSITE_GOOGLE_TAG_MANAGER_ID?.trim()
    const containerId = fromEnv || "GTM-5VB5VHTZ"

    if (!isValidGtmContainerId(containerId)) {
      if (import.meta.env.DEV) {
        console.warn("[analytics] Google Tag Manager: invalid or missing container ID")
      }
      return
    }

    if (document.getElementById(GTM_SCRIPT_ID)) {
      return
    }

    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      "event": "gtm.js",
    })

    const firstScript = document.getElementsByTagName("script")[0]
    const gtmScript = document.createElement("script")
    gtmScript.id = GTM_SCRIPT_ID
    gtmScript.async = true
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`
    gtmScript.onerror = () => {
      console.info("[analytics] Google Tag Manager script blocked or failed to load")
    }

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(gtmScript, firstScript)
    } else {
      document.head.appendChild(gtmScript)
    }
  } catch (error) {
    console.info("[analytics] Google Tag Manager init skipped due to runtime restriction", error)
  }
}

export const pushDataLayerEvent = (event: string, params?: Record<string, unknown>): void => {
  try {
    if (typeof window === "undefined") {
      return
    }

    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push({
      event,
      ...params,
    })
  } catch {
    // Ignore dataLayer push errors (e.g. privacy extensions).
  }
}

export const removeGoogleTagManagerScript = (): void => {
  try {
    document.getElementById(GTM_SCRIPT_ID)?.remove()
  } catch {
    // Ignore DOM errors during teardown.
  }
}
